/**
 * Service-history estimates (spec §SIQ, rules-based and explainable).
 * For a person/address, how often did past attempts in the same time of day
 * end in personal or substitute service? Too little history → "NSH"
 * (no service history), which is its own state, not a low score (SIQ-008).
 */
import { localParts } from './time.ts';
import type { LatLng } from './geo.ts';

export type DayWindow = 'morning' | 'afternoon' | 'evening';
export const WINDOW_LABELS: Record<DayWindow, string> = { morning: 'before noon', afternoon: 'noon–6 PM', evening: 'after 6 PM' };

export function timeWindowOf(at: Date, tz: string): DayWindow {
  const h = localParts(at, tz).hour;
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

export interface WindowStats {
  n: number;
  personal: number;
  substitute: number;
}
export type ServeStats = Record<DayWindow, WindowStats> & { total: WindowStats };

const empty = (): WindowStats => ({ n: 0, personal: 0, substitute: 0 });

/** Aggregate attempts (already filtered to one person/address, voided removed). */
export function computeServeStats(attempts: { attemptedAt: Date | string; outcome: string; method: string | null }[], tz: string): ServeStats {
  const s: ServeStats = { morning: empty(), afternoon: empty(), evening: empty(), total: empty() };
  for (const a of attempts) {
    const w = timeWindowOf(new Date(a.attemptedAt), tz);
    for (const b of [s[w], s.total]) {
      b.n++;
      if (a.outcome === 'served' && a.method === 'personal') b.personal++;
      if (a.outcome === 'served' && a.method === 'substitute') b.substitute++;
    }
  }
  return s;
}

export interface ServeEstimate {
  /** 0–100, or null when there isn't enough history. */
  personalPct: number | null;
  substitutePct: number | null;
  basis: 'window' | 'overall' | 'nsh';
  window: DayWindow;
  n: number;
}

/** Uses the current time-of-day window when it has enough attempts, else all history, else NSH. */
export function estimateService(stats: ServeStats | null | undefined, at: Date, tz: string, minAttempts = 3): ServeEstimate {
  const window = timeWindowOf(at, tz);
  const pick = (b: WindowStats, basis: 'window' | 'overall'): ServeEstimate => ({
    personalPct: Math.round((b.personal / b.n) * 100),
    substitutePct: Math.round((b.substitute / b.n) * 100),
    basis,
    window,
    n: b.n,
  });
  if (stats && stats[window].n >= minAttempts) return pick(stats[window], 'window');
  if (stats && stats.total.n >= minAttempts) return pick(stats.total, 'overall');
  return { personalPct: null, substitutePct: null, basis: 'nsh', window, n: stats?.total.n ?? 0 };
}

// ─── Navigation helpers for the in-car screen ───────────────────────────────

/** Initial compass bearing from a to b, degrees 0–360. */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const r = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lng - a.lng) * r);
  return ((Math.atan2(y, x) / r) + 360) % 360;
}

/**
 * Which side of the car a destination is on, given the direction of travel.
 * Only meaningful when moving and fairly close; otherwise null.
 */
export function sideOfTravel(heading: number | null, from: LatLng, to: LatLng): 'left' | 'right' | 'ahead' | 'behind' | null {
  if (heading == null || !Number.isFinite(heading)) return null;
  let rel = bearingDeg(from, to) - heading;
  rel = ((rel + 540) % 360) - 180; // −180…180, positive = clockwise = right
  if (Math.abs(rel) <= 12) return 'ahead';
  if (Math.abs(rel) >= 150) return 'behind';
  return rel > 0 ? 'right' : 'left';
}
